/**
 * useProtectedImage — fetches a signed image URL and converts it to a
 * same-tab blob URL so the real URL is never visible in the DOM.
 *
 * Blob URLs (blob:https://...) are:
 *   - Bound to the current tab's lifetime — gone when the tab closes
 *   - Not transferable between tabs or sessions
 *   - Not resolvable from a different origin
 *
 * The signed URL is sent with `credentials: 'include'` so the Laravel
 * session cookie is passed, enabling future per-user access control if needed.
 *
 * Usage:
 *   const { src, loading, error } = useProtectedImage(photo.thumb);
 *   <img src={src ?? undefined} />
 *
 * Notes:
 *   - Returns the signed URL immediately as a fallback while the blob loads.
 *   - Non-proxied URLs (CDN etc.) are returned as-is with no fetch.
 *   - Blob URLs are NOT revoked on unmount — they stay cached for the tab's
 *     lifetime. Thumbnails are small (~20–50 KB each); revoking on unmount
 *     would kill the cache and force re-fetches when cards scroll in/out.
 *   - Call prefetchImages(urls) before render to pre-warm the cache.
 */
import { useEffect, useRef, useState } from 'react';

// Module-level cache: signed URL → blob URL.
// Persists for the tab's lifetime (blob URLs are freed automatically when the
// tab closes). Not revoked on unmount so re-mounting a card costs zero fetches.
const blobCache = new Map<string, string>();

// In-flight promise cache: prevents duplicate concurrent fetches for the same URL.
const inflightCache = new Map<string, Promise<string>>();

function isProtectedUrl(url: string): boolean {
  // Laravel proxy route: /img/{id}/{conversion?}?signature=...
  if (url.includes('/img/') && url.includes('signature=')) return true;
  // S3 / Scaleway presigned URLs contain X-Amz-Signature query param
  if (url.includes('X-Amz-Signature=')) return true;
  return false;
}

function fetchBlob(url: string): Promise<string> {
  const cached = inflightCache.get(url);
  if (cached) return cached;

  const promise = fetch(url, { credentials: 'include' })
    .then((r) => {
      if (!r.ok) throw new Error(`${r.status}`);
      return r.blob();
    })
    .then((blob) => {
      const blobUrl = URL.createObjectURL(blob);
      blobCache.set(url, blobUrl);
      inflightCache.delete(url);
      return blobUrl;
    })
    .catch((err) => {
      inflightCache.delete(url);
      throw err;
    });

  inflightCache.set(url, promise);
  return promise;
}

/**
 * Pre-warm the blob cache for a list of signed URLs.
 * Call this as early as possible (e.g. in the page component before render)
 * so blob fetches run in parallel before cards mount.
 */
export function prefetchImages(urls: (string | null | undefined)[]): void {
  for (const url of urls) {
    if (url && isProtectedUrl(url) && !blobCache.has(url) && !inflightCache.has(url)) {
      fetchBlob(url).catch(() => { /* silent — hook will retry */ });
    }
  }
}

interface ProtectedImageState {
  src: string | null;
  loading: boolean;
  error: boolean;
}

export function useProtectedImage(
  signedUrl: string | null | undefined,
): ProtectedImageState {
  const [state, setState] = useState<ProtectedImageState>(() => {
    // Serve from cache synchronously if already fetched this session
    if (signedUrl && isProtectedUrl(signedUrl) && blobCache.has(signedUrl)) {
      return { src: blobCache.get(signedUrl)!, loading: false, error: false };
    }
    // Non-proxied URL (external CDN etc.) — use as-is, no fetch needed
    if (signedUrl && !isProtectedUrl(signedUrl)) {
      return { src: signedUrl, loading: false, error: false };
    }
    // For proxied URLs: show the signed URL immediately as a fallback so images
    // appear at once while the blob fetch runs silently in the background.
    return { src: signedUrl ?? null, loading: !!signedUrl, error: false };
  });

  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!signedUrl) {
      setState({ src: null, loading: false, error: false });
      return;
    }

    // Non-proxied URL — use directly
    if (!isProtectedUrl(signedUrl)) {
      setState({ src: signedUrl, loading: false, error: false });
      return;
    }

    // Synchronous cache hit — no setState needed if already initialised correctly,
    // but guard for URL-change case where previous state had a different blob.
    if (blobCache.has(signedUrl)) {
      setState({ src: blobCache.get(signedUrl)!, loading: false, error: false });
      return;
    }

    // Abort previous in-flight request when URL changes
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    // Keep signed URL visible as fallback while blob loads — no blank flash.
    setState((prev) => ({ src: prev.src ?? signedUrl, loading: true, error: false }));

    fetchBlob(signedUrl)
      .then((blobUrl) => {
        if (ctrl.signal.aborted) return;
        setState({ src: blobUrl, loading: false, error: false });
      })
      .catch((err) => {
        if (ctrl.signal.aborted || err.name === 'AbortError') return;
        setState((prev) => ({ ...prev, loading: false, error: true }));
      });

    return () => {
      // Only cancel the fetch subscription — do NOT revoke the blob URL.
      // The blob stays in blobCache for the tab lifetime so re-mounting the
      // card (e.g. after scrolling) is a free cache hit.
      ctrl.abort();
    };
  }, [signedUrl]);

  return state;
}
